import { Banknote, Landmark, Pencil, Trash2, Undo2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { ErrorState } from '@/components/common/States'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { DateField } from '@/components/ui/date-field'
import { Input } from '@/components/ui/input'
import { confirmModal, Modal } from '@/components/ui/modal'
import { Select } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { fmtDate, fmtMoney } from '../../../shared/format'
import { useAccounts } from '../../api'
import {
  useDeleteLoan,
  useEditInstallment,
  useLoan,
  usePayInstallment,
  useUnpayInstallment,
  type InstallmentEdit,
  type Loan,
  type LoanInstallment,
  type LoanKind,
  type LoanPerms,
  textKeys,
} from '../../loans'
import { toastError } from '../../ui'
import { Footer } from '../AccountForms'
import { Field, FormSection } from '../FormBits'
import { BankStatementsPanel } from './BankStatementsPanel'
import { PlanTable } from './LoanForm'

/**
 * Kredinin ödeme planı: sıradaki taksit ödenir (vadesi geçmişse gecikme faizi + vergiler eklenir), son ödeme geri
 * alınabilir, bekleyen taksit bankanın planına göre düzeltilebilir. Hiç taksiti ödenmemiş kredi silinebilir.
 */
export function LoanDetailModal({
  kind,
  id,
  perms,
  onClose,
}: {
  kind: LoanKind
  id: string
  perms: LoanPerms
  onClose: () => void
}) {
  const { t } = useTranslation()
  const k = (key: string) => textKeys(kind, key)
  const loan = useLoan(kind, id)
  const del = useDeleteLoan(kind)
  const unpay = useUnpayInstallment(kind, id)
  const [paying, setPaying] = useState<LoanInstallment | null>(null)
  const [editing, setEditing] = useState<LoanInstallment | null>(null)
  const l = loan.data
  const nextPending = l?.installments.find((i) => i.status === 'pending')
  const lastPaid = l ? [...l.installments].reverse().find((i) => i.status === 'paid') : undefined

  return (
    <Modal
      open
      size="2xl"
      onOpenChange={(v) => !v && onClose()}
      icon={<Landmark />}
      title={l ? l.name : t(k('schedule'))}
      description={
        l
          ? `${l.bankName ? `${l.bankName} · ` : ''}${fmtMoney(l.principal)} · ${t(k('rateShort'), { rate: l.monthlyRate })}`
          : undefined
      }
      footer={
        <>
          {perms.delete && l && l.paidCount === 0 ? (
            <Button
              type="button"
              variant="ghost"
              className="text-destructive mr-auto"
              disabled={del.isPending}
              onClick={() =>
                void confirmModal({
                  title: t(k('deleteTitle')),
                  description: t(l.scope === 'company' ? k('deleteBodyCompany') : k('deleteBody')),
                  confirmLabel: t('common.delete'),
                  destructive: true,
                }).then(
                  (ok) =>
                    ok &&
                    del.mutate(l.id, {
                      onSuccess: () => {
                        toast.success(t(k('deleted')))
                        onClose()
                      },
                      onError: toastError,
                    }),
                )
              }
            >
              <Trash2 />
              {t('common.delete')}
            </Button>
          ) : null}
          <Button type="button" variant="cancel" onClick={onClose}>
            {t('common.close')}
          </Button>
        </>
      }
    >
      {loan.isError ? (
        <ErrorState error={loan.error} onRetry={() => void loan.refetch()} />
      ) : !l ? (
        <Skeleton className="h-64" />
      ) : (
        <div className="space-y-4">
          <Summary loan={l} />
          <PlanTable
            rows={l.installments}
            action={(r) =>
              r.status === 'paid' ? (
                <span className="inline-flex items-center gap-1">
                  <Badge
                    tone="success"
                    title={
                      Number(r.lateInterest) > 0
                        ? t(k('lateIncluded'), {
                            amount: fmtMoney((Number(r.lateInterest) + Number(r.lateTax)).toFixed(2)),
                          })
                        : undefined
                    }
                  >
                    {t(k('paidOn'), { date: fmtDate(r.paidOn) })}
                  </Badge>
                  {perms.unpay && r.id === lastPaid?.id ? (
                    <Button
                      size="icon"
                      variant="ghost"
                      aria-label={t(k('unpay'))}
                      title={t(k('unpay'))}
                      disabled={unpay.isPending}
                      onClick={() =>
                        void confirmModal({
                          title: t(k('unpayTitle')),
                          description: t(k('unpayBody')),
                          confirmLabel: t(k('unpay')),
                          destructive: true,
                        }).then(
                          (ok) =>
                            ok &&
                            unpay.mutate(r.id, {
                              onSuccess: () => toast.success(t(k('unpaid'))),
                              onError: toastError,
                            }),
                        )
                      }
                    >
                      <Undo2 className="size-4" />
                    </Button>
                  ) : null}
                </span>
              ) : perms.pay || perms.edit ? (
                <span className="inline-flex items-center gap-1">
                  {perms.pay && r.id === nextPending?.id ? (
                    <Button size="sm" variant={r.overdue ? 'destructive' : 'outline'} onClick={() => setPaying(r)}>
                      <Banknote />
                      {t(k('pay'))}
                    </Button>
                  ) : null}
                  {perms.edit ? (
                    <Button
                      size="icon"
                      variant="ghost"
                      aria-label={t(k('edit'))}
                      title={t(k('edit'))}
                      onClick={() => setEditing(r)}
                    >
                      <Pencil className="size-4" />
                    </Button>
                  ) : null}
                </span>
              ) : r.overdue ? (
                <Badge tone="danger">{t(k('overdueOne'))}</Badge>
              ) : null
            }
          />
          {l.note ? <p className="text-muted-foreground text-xs whitespace-pre-wrap">{l.note}</p> : null}
          <section className="space-y-2 border-t pt-4">
            <h3 className="text-sm font-semibold">{t('finance.bankStatements.title')}</h3>
            <BankStatementsPanel type={l.kind} id={l.id} scope={l.scope} />
          </section>
        </div>
      )}
      {paying && l ? <PayModal loan={l} row={paying} onDone={() => setPaying(null)} /> : null}
      {editing && l ? <EditModal kind={kind} loanId={l.id} row={editing} onDone={() => setEditing(null)} /> : null}
    </Modal>
  )
}

function Summary({ loan: l }: { loan: Loan }) {
  const { t } = useTranslation()
  const k = (key: string) => textKeys(l.kind, key)
  const fees = l.fees.reduce((a, f) => a + Number(f.amount), 0)
  return (
    <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs sm:grid-cols-4">
      {[
        [t(k('remainingPrincipal')), fmtMoney(l.remainingPrincipal)],
        [t(k('remainingTotal')), fmtMoney(l.remainingTotal)],
        [t(k('progressLabel')), t(k('progress'), { paid: l.paidCount, total: l.installmentCount })],
        [t(k('form.disbursedOn')), fmtDate(l.disbursedOn)],
        ['BSMV / KKDF', `%${l.bsmvRate} / %${l.kkdfRate}`],
        [t(k('form.lateRate')), `%${l.lateRate}`],
        [t(k('fees')), fees > 0 ? fmtMoney(fees.toFixed(2)) : '—'],
        [t(k('statusLabel')), t(l.status === 'closed' ? k('closed') : k('active'))],
      ].map(([label, value]) => (
        <div key={label}>
          <dt className="text-muted-foreground">{label}</dt>
          <dd className="text-sm tabular-nums">{value}</dd>
        </div>
      ))}
    </dl>
  )
}

/** Taksit ödeme: gecikme faizi sunucuda hesaplanır; banka farklı tahsil ettiyse elle girilir. */
function PayModal({ loan, row, onDone }: { loan: Loan; row: LoanInstallment; onDone: () => void }) {
  const { t } = useTranslation()
  const k = (key: string) => textKeys(loan.kind, key)
  const pay = usePayInstallment(loan.kind, loan.id)
  const company = loan.scope === 'company'
  const accounts = (useAccounts().data ?? []).filter((a) => a.active && (a.type === 'cash' || a.type === 'bank'))
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10))
  const [accountId, setAccountId] = useState(loan.disbursementAccountId)
  const [manual, setManual] = useState(false)
  const [lateInterest, setLateInterest] = useState('')
  const [lateTax, setLateTax] = useState('')
  const late = date > row.dueDate
  const missingAccount = company && !accountId

  return (
    <Modal
      open
      onOpenChange={(open) => !open && onDone()}
      dismissible={!pay.isPending}
      icon={<Banknote />}
      title={t(k('payTitle'), { no: row.no, total: loan.installmentCount })}
      description={t(company ? k('payHintCompany') : k('payHint'), {
        amount: fmtMoney(row.total),
        date: fmtDate(row.dueDate),
      })}
      onSubmit={(e) => {
        e.preventDefault()
        if (missingAccount) return
        pay.mutate(
          {
            id: row.id,
            input: {
              date,
              accountId,
              lateInterest: manual ? lateInterest || '0' : '',
              lateTax: manual ? lateTax || '0' : '',
            },
          },
          {
            onSuccess: () => {
              toast.success(t(k('paid')))
              onDone()
            },
            onError: toastError,
          },
        )
      }}
      footer={<Footer pending={pay.isPending} disabled={missingAccount} onCancel={onDone} />}
    >
      <FormSection>
        <Field id="lp-date" label={t(k('payDate'))}>
          <DateField id="lp-date" value={date} onChange={setDate} clearable={false} />
        </Field>
        {company ? (
          <Field id="lp-acc" label={t(k('payAccount'))}>
            <Select
              id="lp-acc"
              value={accountId}
              onChange={setAccountId}
              placeholder={t('common.select')}
              options={accounts.map((a) => ({ value: a.id, label: a.name }))}
            />
          </Field>
        ) : null}
        {loan.repayment === 'bullet' && date < row.dueDate ? (
          <p className="text-muted-foreground text-xs sm:col-span-2">{t('finance.cashAdvances.earlyNotice')}</p>
        ) : null}
        {late ? (
          <div className="space-y-2 sm:col-span-2">
            <p className="text-warning text-xs">{t(k('lateNotice'), { rate: loan.lateRate })}</p>
            <label className="flex items-center gap-1.5 text-xs">
              <input type="checkbox" checked={manual} onChange={(e) => setManual(e.target.checked)} />
              {t(k('lateManual'))}
            </label>
          </div>
        ) : null}
        {late && manual ? (
          <>
            <Field id="lp-li" label={t(k('lateInterest'))}>
              <Input
                id="lp-li"
                inputMode="decimal"
                value={lateInterest}
                onChange={(e) => setLateInterest(e.target.value)}
              />
            </Field>
            <Field id="lp-lt" label={t(k('lateTax'))}>
              <Input id="lp-lt" inputMode="decimal" value={lateTax} onChange={(e) => setLateTax(e.target.value)} />
            </Field>
          </>
        ) : null}
      </FormSection>
    </Modal>
  )
}

/** Bekleyen taksidi bankanın planına göre düzeltme (sonraki kalan anaparalar sunucuda yeniden hesaplanır). */
function EditModal({
  kind,
  loanId,
  row,
  onDone,
}: {
  kind: LoanKind
  loanId: string
  row: LoanInstallment
  onDone: () => void
}) {
  const { t } = useTranslation()
  const k = (key: string) => textKeys(kind, key)
  const edit = useEditInstallment(kind, loanId)
  const [v, setV] = useState<InstallmentEdit>({
    dueDate: row.dueDate,
    principal: row.principal,
    interest: row.interest,
    bsmv: row.bsmv,
    kkdf: row.kkdf,
  })
  const nums = [v.principal, v.interest, v.bsmv, v.kkdf].map((x) => Number(x.replace(',', '.')))
  const valid = !!v.dueDate && nums.every((x) => x >= 0)
  const total = valid ? nums.reduce((a, b) => a + b, 0).toFixed(2) : ''
  const field = (k: keyof InstallmentEdit, label: string) => (
    <Field id={`le-${k}`} label={label}>
      <Input
        id={`le-${k}`}
        inputMode="decimal"
        value={v[k]}
        onChange={(e) => setV((x) => ({ ...x, [k]: e.target.value }))}
      />
    </Field>
  )

  return (
    <Modal
      open
      onOpenChange={(open) => !open && onDone()}
      dismissible={!edit.isPending}
      icon={<Pencil />}
      title={t(k('editTitle'), { no: row.no })}
      description={t(k('editHint'))}
      onSubmit={(e) => {
        e.preventDefault()
        if (!valid) return
        edit.mutate(
          { id: row.id, input: v },
          {
            onSuccess: () => {
              toast.success(t(k('edited')))
              onDone()
            },
            onError: toastError,
          },
        )
      }}
      footer={<Footer pending={edit.isPending} disabled={!valid} onCancel={onDone} />}
    >
      <FormSection>
        <Field id="le-due" label={t(k('dueDate'))}>
          <DateField
            id="le-due"
            value={v.dueDate}
            onChange={(d) => setV((x) => ({ ...x, dueDate: d }))}
            clearable={false}
          />
        </Field>
        {field('principal', t(k('principal')))}
        {field('interest', t(k('interest')))}
        {field('bsmv', 'BSMV')}
        {field('kkdf', 'KKDF')}
        <p className="text-muted-foreground self-end text-sm sm:col-span-2">
          {t(k('installment'))}:{' '}
          <span className="text-foreground font-semibold tabular-nums">{total ? fmtMoney(total) : '—'}</span>
        </p>
      </FormSection>
    </Modal>
  )
}
